import { useState } from 'react';
import { motion } from 'framer-motion';
import { Target, UserX, Award, MapPin, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LeadFormModal } from '@/components/LeadFormModal';
import { IMAGES } from '@/assets/images';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';

export default function Home() {
  const [modalOpen, setModalOpen] = useState(false);

  const targetAudience = [
    {
      description: 'Empresários, líderes e profissionais de 25 a 50 anos que sentem um vazio interno mesmo com estabilidade financeira'
    },
    {
      description: 'Pessoas que vivem para agradar os outros, evitam conflitos e carregam culpa'
    },
    {
      description: 'Quem quer romper amarras emocionais, se reconectar com Deus e viver com propósito'
    },
    {
      description: 'Quem deseja ser líder corajoso, autêntico e construir uma identidade sólida'
    }
  ];

  const notForYou = [
    {
      description: 'Pessoas que não estão dispostas a olhar para suas emoções com coragem'
    },
    {
      description: 'Quem acredita que "isso é só mais uma motivação momentânea"'
    },
    {
      description: 'Quem prefere continuar na zona de conforto e não quer assumir protagonismo'
    },
    {
      description: 'Quem não está aberto a mudanças profundas e crescimento pessoal'
    }
  ];

  return (
    <>
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://valtermoraes.com/wp-content/uploads/2025/06/Imagem-do-WhatsApp-de-2025-05-19-a_s_-11.09.32_871309ea.webp" 
            alt="Background" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/90" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-black/60" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="max-w-4xl mx-auto text-center space-y-8"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
          >
            <div className="mb-6 md:mb-8">
              <img 
                src="https://eusouantoniofilho.com.br/wp-content/uploads/2025/06/METODO-EXODO.png" 
                alt="Método Êxodo" 
                className="w-48 md:w-64 mx-auto"
              />
            </div>

            <motion.h1 
              className="text-xl md:text-2xl lg:text-3xl font-bold mb-6 md:mb-8 leading-tight text-white drop-shadow-lg max-w-3xl mx-auto px-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.1 }}
            >
              Desperte sua identidade, liberte sua mente e construa um legado de <span className="text-primary">riqueza e fé</span> para sua família.
            </motion.h1>

            <div className="aspect-video max-w-3xl mx-auto rounded-xl md:rounded-2xl overflow-hidden shadow-2xl border border-primary/20 bg-card/50 p-2 md:p-4">
              <div className="aspect-video bg-muted rounded-lg md:rounded-xl overflow-hidden">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/Eh5JXKc23lE"
                  title="Método Êxodo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </div>

            <div className="space-y-4 md:space-y-6">
              <motion.p 
                className="text-base md:text-lg text-foreground/90 max-w-3xl mx-auto px-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ ...springPresets.gentle, delay: 0.2 }}
              >
                Assista ao vídeo e descubra o que o <span className="text-primary font-semibold">Método Êxodo</span> pode fazer por você — o caminho real para transformar sua vida emocional, espiritual e profissional.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...springPresets.gentle, delay: 0.3 }}
              >
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-primary via-yellow-400 to-primary hover:from-yellow-400 hover:to-primary hover:scale-105 transition-all text-sm md:text-base px-4 md:px-6 py-3 md:py-4 shadow-lg shadow-primary/30 text-black font-bold w-full md:w-auto whitespace-normal leading-tight"
                  onClick={() => setModalOpen(true)}
                >
                  Quero saber como funciona o Método Êxodo
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="location" className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-5xl mx-auto"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-3xl overflow-hidden border border-blue-500/30 shadow-2xl">
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/2">
                  <img 
                    src="https://valtermoraes.com/wp-content/uploads/2025/06/Ponte-Estaiada-Sao-Paulo-shutterstock_1420355708-1.webp" 
                    alt="São Paulo - Ponte Estaiada" 
                    className="w-full h-64 md:h-full object-cover"
                  />
                </div>
                <div className="md:w-1/2 p-6 md:p-8 flex flex-col justify-center">
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2">SÃO PAULO/SP</h2>
                  <p className="text-base md:text-lg text-blue-200 mb-6 md:mb-8">20 a 22 de Novembro de 2026</p>
                  <h1 className="text-xl md:text-2xl lg:text-3xl font-bold mb-4 md:mb-6 leading-tight text-white drop-shadow-lg">
                    Desperte sua identidade, liberte sua mente e construa um legado de <span className="text-primary">riqueza e fé</span> para sua família.
                  </h1>
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-primary via-yellow-400 to-primary hover:from-yellow-400 hover:to-primary hover:scale-105 transition-all text-sm md:text-base px-4 md:px-6 py-3 md:py-4 shadow-lg shadow-primary/30 text-black font-bold w-full whitespace-normal leading-tight"
                    onClick={() => setModalOpen(true)}
                  >
                    QUERO VIVER ESSA TRANSFORMAÇÃO
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="para-quem" className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.div 
              className="text-center space-y-4 mb-12"
              variants={staggerItem}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-primary px-4">PARA QUEM É ESSA IMERSÃO</h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
              {targetAudience.map((item, index) => (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  className="bg-card rounded-xl p-4 md:p-6 border border-primary/20"
                >
                  <div className="flex items-start gap-3 md:gap-4">
                    <div className="p-2 md:p-3 rounded-xl bg-primary/10 flex-shrink-0">
                      <Target className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                    </div>
                    <p className="text-sm md:text-base text-foreground/90 flex-1">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div 
              className="text-center"
              variants={staggerItem}
            >
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-primary via-yellow-400 to-primary hover:from-yellow-400 hover:to-primary hover:scale-105 transition-all text-sm md:text-base px-4 md:px-6 py-3 md:py-4 shadow-lg shadow-primary/30 text-black font-bold w-full md:w-auto whitespace-normal leading-tight"
                onClick={() => setModalOpen(true)}
              >
                Quero saber como funciona o Método Êxodo
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section id="nao-para-quem" className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.div 
              className="text-center space-y-4 mb-12"
              variants={staggerItem}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-red-400 px-4">PARA QUEM NÃO É ESSA IMERSÃO</h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {notForYou.map((item, index) => (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  className="bg-card rounded-xl p-4 md:p-6 border border-red-400/20"
                >
                  <div className="flex items-start gap-3 md:gap-4">
                    <div className="p-2 md:p-3 rounded-xl bg-destructive/10 flex-shrink-0">
                      <UserX className="w-5 h-5 md:w-6 md:h-6 text-red-400" />
                    </div>
                    <p className="text-sm md:text-base text-foreground/90 flex-1">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section id="mentor" className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.div 
              className="text-center space-y-4 mb-12"
              variants={staggerItem}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-primary px-4">QUEM É SEU MENTOR</h2>
            </motion.div>

            <motion.div 
              className="bg-gradient-to-r from-card to-muted/50 rounded-xl md:rounded-2xl p-6 md:p-8 border border-primary/20 mb-8 md:mb-12"
              variants={staggerItem}
            >
              <div className="text-center md:text-left md:flex md:items-center md:space-x-8">
                <div className="mb-6 md:mb-0">
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-full mx-auto md:mx-0 overflow-hidden border-4 border-primary/30">
                    <img 
                      src="https://valtermoraes.com/wp-content/uploads/2025/06/Inserir-um-titulo-1.jpg" 
                      alt="Valter Moraes" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-bold mb-2 text-primary">Valter Moraes</h3>
                  <h4 className="text-xl md:text-2xl font-bold text-primary mb-3 md:mb-4">Método Êxodo</h4>
                  <p className="text-lg md:text-xl text-foreground/80 mb-3 md:mb-4">Especialista em Inteligência Emocional</p>
                  <p className="text-sm md:text-base text-foreground/90 leading-relaxed">
                    Com mais de 10 anos ajudando empresários e líderes a superarem bloqueios emocionais, reconectarem com seu propósito e transformarem vidas, Valter é seu guia nesta jornada rumo à liberdade interior e sucesso verdadeiro.
                  </p>
                  <p className="text-sm md:text-base text-foreground/70 mt-3 md:mt-4">
                    Transformando vidas através da Inteligência Emocional
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="text-center"
              variants={staggerItem}
            >
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-primary via-yellow-400 to-primary hover:from-yellow-400 hover:to-primary hover:scale-105 transition-all text-sm md:text-base px-4 md:px-6 py-3 md:py-4 shadow-lg shadow-primary/30 text-black font-bold w-full md:w-auto whitespace-normal leading-tight"
                onClick={() => setModalOpen(true)}
              >
                Quero saber como funciona o Método Êxodo
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <LeadFormModal open={modalOpen} onOpenChange={setModalOpen} />
    </>
  );
}